import { ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import ExportButtons from '../../../components/ExportButtons'
import type { ColumnDefinition, ExportMetadata } from '../../../utils/exportToCsv'
import type { StaatidForhandlerRow, ForhandlerSort } from './api'
import { formatAndel, formatDager } from './api'

interface Props {
  rows: StaatidForhandlerRow[]
  total: number
  offset: number
  limit: number
  sort: ForhandlerSort
  sortDir: 'asc' | 'desc'
  onSortChange: (sort: ForhandlerSort) => void
  onPageChange: (offset: number) => void
  onRowClick?: (orgNr: string, navn: string | null) => void
  loading?: boolean
  exportMeta?: Partial<ExportMetadata>
}

// Built at render so the headers follow the UI language (#714).
const exportColumns = (t: TFunction<'staatid'>): ColumnDefinition[] => [
  { key: 'rang', label: t('common.rank') },
  { key: 'dealer_org_navn', label: t('common.dealer') },
  { key: 'dealer_org_nr', label: t('common.orgNr') },
  { key: 'antall_roundtrips', label: t('common.antall'), format: (v: number) => v.toLocaleString('nb-NO') },
  { key: 'median_dager', label: t('common.medianDager'), format: (v: number | null) => (v === null ? '-' : String(v)) },
  { key: 'snitt_dager', label: t('common.snittDager'), format: (v: number | null) => (v === null ? '-' : String(v)) },
  { key: 'median_siste_ledd_dager', label: t('common.medianEgneDager'), format: (v: number | null) => (v === null ? '-' : String(v)) },
  { key: 'andel_kommisjon', label: t('common.kommisjonssalg'), format: (v: number) => formatAndel(v) },
]

const SORTABLE = [
  { key: 'antall_roundtrips', labelKey: 'common.antall' },
  { key: 'median_dager', labelKey: 'common.medianDager' },
] as const satisfies readonly { key: ForhandlerSort; labelKey: string }[]

export default function ForhandlerTable({
  rows, total, offset, limit, sort, sortDir, onSortChange, onPageChange, onRowClick, loading, exportMeta,
}: Props) {
  const { t } = useTranslation('staatid')
  if (loading) {
    return (
      <div className="card p-4">
        <div data-testid="table-skeleton" className="animate-pulse h-64 bg-gray-100 dark:bg-dark-border rounded-sm" />
      </div>
    )
  }

  const exportRows = rows.map((row, i) => ({ rang: offset + i + 1, ...row }))
  const page = Math.floor(offset / limit) + 1
  const pages = Math.max(1, Math.ceil(total / limit))

  function sortIcon(key: ForhandlerSort) {
    if (sort !== key) return <ChevronsUpDown className="w-3.5 h-3.5 inline opacity-40" />
    return sortDir === 'desc'
      ? <ArrowDown className="w-3.5 h-3.5 inline" />
      : <ArrowUp className="w-3.5 h-3.5 inline" />
  }

  return (
    <div data-testid="staatid-forhandler-table" className="card overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('forhandlere.tableTitle', { n: total.toLocaleString('nb-NO') })}
        </span>
        <ExportButtons
          data={exportRows}
          columns={exportColumns(t)}
          metadata={{ title: t('exportTitles.dealers'), ...exportMeta }}
        />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card">
              <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('common.rank')}</th>
              <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('common.dealer')}</th>
              <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('common.orgNr')}</th>
              {SORTABLE.map(({ key, labelKey }) => (
                <th
                  key={key}
                  onClick={() => onSortChange(key)}
                  className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400 cursor-pointer select-none hover:text-gray-900 dark:hover:text-gray-200"
                >
                  {t(labelKey)} {sortIcon(key)}
                </th>
              ))}
              <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400">{t('common.snittDager')}</th>
              <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400" title={t('forhandlere.medianEgneTitle')}>{t('common.medianEgneDager')}</th>
              <th
                className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400"
                title={t('common.kommisjonForklaring')}
                data-testid="staatid-kommisjon-header"
              >
                {t('common.kommisjonssalg')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={row.dealer_org_nr}
                onClick={() => onRowClick?.(row.dealer_org_nr, row.dealer_org_navn)}
                className={`border-b border-gray-100 dark:border-dark-border hover:bg-gray-100 dark:hover:bg-dark-border transition-colors ${onRowClick ? 'cursor-pointer' : ''} ${i % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''}`}
              >
                <td className="px-4 py-2 text-gray-500 tabular-nums">{offset + i + 1}</td>
                <td className="px-4 py-2">{row.dealer_org_navn ?? '—'}</td>
                <td className="px-4 py-2 font-mono text-xs text-primary-600 dark:text-primary-400">{row.dealer_org_nr}</td>
                <td className="px-4 py-2 text-right tabular-nums">{row.antall_roundtrips.toLocaleString('nb-NO')}</td>
                <td className="px-4 py-2 text-right tabular-nums">{formatDager(row.median_dager)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{formatDager(row.snitt_dager)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{formatDager(row.median_siste_ledd_dager)}</td>
                <td className="px-4 py-2 text-right tabular-nums" data-testid="staatid-kommisjon-cell">
                  {formatAndel(row.andel_kommisjon)}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-gray-400">{t('common.noData')}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between px-4 py-2 border-t border-gray-200 dark:border-dark-border text-sm">
        <span className="text-gray-500 dark:text-gray-400">{t('common.pageOf', { page, pages })}</span>
        <div className="flex gap-2">
          <button
            className="btn-secondary text-xs px-3 py-1 disabled:opacity-40"
            disabled={offset === 0}
            onClick={() => onPageChange(Math.max(0, offset - limit))}
          >
            {t('common.prev')}
          </button>
          <button
            className="btn-secondary text-xs px-3 py-1 disabled:opacity-40"
            disabled={offset + limit >= total}
            onClick={() => onPageChange(offset + limit)}
          >
            {t('common.next')}
          </button>
        </div>
      </div>
    </div>
  )
}
